import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Send, Loader2, MailCheck } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const RichiediInfo = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Compila nome, email e messaggio.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSent(true);
      toast.success("Richiesta inviata! Ti risponderemo al più presto. 💛");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast.error("Ops, qualcosa è andato storto. Riprova o chiamaci.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full rounded-2xl border border-slate-200 bg-white px-5 py-3.5 font-600 text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-rose-300 focus:border-transparent transition-all";

  return (
    <section
      id="richiedi-info"
      data-testid="richiedi-info-section"
      className="py-24 sm:py-32 scroll-mt-24"
    >
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, ease: "backOut" }}
          className="relative rounded-[2.5rem] bg-gradient-to-br from-rose-50 via-amber-50 to-sky-50 border border-rose-100 p-8 sm:p-12 shadow-xl"
        >
          <div className="text-center">
            <p className="text-sm font-800 uppercase tracking-widest text-rose-500">
              Richiedi Informazioni
            </p>
            <h2 className="mt-3 font-heading text-3xl sm:text-4xl font-medium tracking-tight text-slate-800">
              Parliamo del tuo bambino
            </h2>
            <p className="mt-3 text-lg text-slate-600 font-600">
              Compila il modulo: ti ricontatteremo con tutte le informazioni su
              iscrizioni, disponibilità e visite.
            </p>
          </div>

          {sent ? (
            <div
              data-testid="richiedi-info-success"
              className="mt-10 flex flex-col items-center text-center gap-4 rounded-3xl bg-white p-10"
            >
              <span className="grid place-items-center h-16 w-16 rounded-full bg-emerald-100 text-emerald-600">
                <MailCheck className="h-8 w-8" />
              </span>
              <h3 className="font-heading text-2xl font-medium text-slate-800">
                Grazie di cuore! 💛
              </h3>
              <p className="text-slate-600 font-600 max-w-md">
                Abbiamo ricevuto la tua richiesta. Ti risponderemo il prima
                possibile all'indirizzo indicato.
              </p>
              <button
                data-testid="richiedi-info-again"
                onClick={() => setSent(false)}
                className="mt-2 rounded-full bg-slate-800 text-white px-6 py-3 font-700 hover:bg-slate-900 transition-colors"
              >
                Invia un'altra richiesta
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-10 space-y-5" data-testid="richiedi-info-form">
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className="block mb-2 font-700 text-slate-700 text-sm">
                    Nome e cognome *
                  </label>
                  <input
                    data-testid="input-name"
                    type="text"
                    value={form.name}
                    onChange={update("name")}
                    placeholder="Il tuo nome"
                    className={inputCls}
                  />
                </div>
                <div>
                  <label className="block mb-2 font-700 text-slate-700 text-sm">
                    Telefono
                  </label>
                  <input
                    data-testid="input-phone"
                    type="tel"
                    value={form.phone}
                    onChange={update("phone")}
                    placeholder="Il tuo numero"
                    className={inputCls}
                  />
                </div>
              </div>
              <div>
                <label className="block mb-2 font-700 text-slate-700 text-sm">
                  Email *
                </label>
                <input
                  data-testid="input-email"
                  type="email"
                  value={form.email}
                  onChange={update("email")}
                  placeholder="La tua email"
                  className={inputCls}
                />
              </div>
              <div>
                <label className="block mb-2 font-700 text-slate-700 text-sm">
                  Messaggio *
                </label>
                <textarea
                  data-testid="input-message"
                  rows={4}
                  value={form.message}
                  onChange={update("message")}
                  placeholder="Raccontaci cosa ti serve sapere (età del bambino, disponibilità, ecc.)"
                  className={`${inputCls} resize-none`}
                />
              </div>
              <button
                data-testid="richiedi-info-submit"
                type="submit"
                disabled={loading}
                className="group w-full inline-flex items-center justify-center gap-2 rounded-full bg-rose-500 text-white px-7 py-4 font-800 hover:bg-rose-600 hover:-translate-y-0.5 transition-all duration-300 shadow-lg shadow-rose-200 disabled:opacity-60 disabled:cursor-not-allowed disabled:translate-y-0"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin" /> Invio in corso...
                  </>
                ) : (
                  <>
                    Invia richiesta
                    <Send className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
                  </>
                )}
              </button>
              <p className="text-center text-sm text-slate-400 font-600">
                Ti risponderemo alla tua email. Non condivideremo i tuoi dati.
              </p>
            </form>
          )}
        </motion.div>
      </div>
    </section>
  );
};
